import { useEffect, useState } from "react";
import {
  IGetManySurchargeBodyRes,
  IProductSupplierWithCategories,
} from "@evennt-tech/api-v2-external";
import { IProductToCategory } from "@evennt-tech/api-v2-core";
import { groupBy, uniq } from "lodash";
import { apiV2 } from "api/firebase";
import { IStage } from "@evennt-tech/api-v2-core/dist/surcharge/surcharge.interface";

export type Stages = [IStage[], string[]];

export const OVERWRITTEN_PRODUCTS_PAGE_SIZE = 20;

export const useComposeStagesAndOverwritten = (
  surcharges: IGetManySurchargeBodyRes,
  totalProductsCount: number
): Stages => {
  if (!surcharges.length) {
    return [[], []];
  }
  if (totalProductsCount === 1) {
    return [surcharges?.sort((p) => p.daysBeforeBuild) || [], []];
  }

  const groupedSurcharges = groupBy(surcharges, "productId");
  const overwrittenProductIds = Object.keys(groupedSurcharges);

  const isEachProductRepresented =
    overwrittenProductIds.length === totalProductsCount;

  const firstProductSurcharges = surcharges
    .filter((surcharge) => surcharge.productId === overwrittenProductIds[0])
    .sort((p) => p.daysBeforeBuild);

  const isEveryStageSame = Object.entries(groupedSurcharges).reduce<boolean>(
    (acc, [_, productSurcharges]) => {
      if (productSurcharges.length !== firstProductSurcharges.length) {
        return false;
      }
      const sortedSurcharges = productSurcharges.sort((p) => p.daysBeforeBuild);
      return (
        acc &&
        sortedSurcharges.reduce<boolean>((surchargeAcc, surcharge, index) => {
          const targetSurcharge = firstProductSurcharges[index];
          return (
            surchargeAcc &&
            targetSurcharge?.type === surcharge.type &&
            targetSurcharge?.daysBeforeBuild === surcharge.daysBeforeBuild &&
            targetSurcharge?.amount === surcharge.amount
          );
        }, true)
      );
    },
    true
  );

  if (isEveryStageSame && isEachProductRepresented) {
    return [firstProductSurcharges || [], []];
  }

  return [[], overwrittenProductIds];
};

const getMainCategoryNames = (
  categories: IProductToCategory[] = []
): string[] =>
  uniq(
    categories
      .map((category) => category?.mainCategory?.name)
      .filter((name): name is string => !!name)
  );

const formatProductOverwrittenLabel = (
  product: IProductSupplierWithCategories
): string => {
  const categoryNames = getMainCategoryNames(product.categories);

  return categoryNames.length
    ? `${product.name} (${categoryNames.join(", ")})`
    : product.name;
};

type OverwrittenProductsArgs = {
  productIds: string[];
  supplierCompanyId: string;
  showId?: string;
  isEnabled: boolean;
};

type OverwrittenProducts = {
  labels: string[];
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
};

export const useOverwrittenProducts = ({
  productIds,
  supplierCompanyId,
  showId,
  isEnabled,
}: OverwrittenProductsArgs): OverwrittenProducts => {
  const [products, setProducts] = useState<IProductSupplierWithCategories[]>(
    []
  );
  const [totalItems, setTotalItems] = useState(0);
  const [requestedPage, setRequestedPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const productIdsKey = productIds.join(",");

  useEffect(() => {
    setProducts([]);
    setTotalItems(0);
    setRequestedPage(0);
  }, [productIdsKey, supplierCompanyId, showId]);

  useEffect(() => {
    if (isEnabled && !requestedPage) setRequestedPage(1);
  }, [isEnabled, requestedPage]);

  useEffect(() => {
    const ids = productIdsKey ? productIdsKey.split(",") : [];

    if (!requestedPage || !ids.length || !supplierCompanyId) return;

    let isActive = true;

    const loadProducts = async () => {
      setIsLoading(true);

      try {
        const {
          items,
          meta,
        } = await apiV2.product.supplier.getManyWithCategoriesByIds({
          productIds: ids,
          supplierCompanyId,
          ...(showId && { showId }),
          page: requestedPage,
          limit: OVERWRITTEN_PRODUCTS_PAGE_SIZE,
        });

        if (!isActive) return;

        setProducts((prev) =>
          requestedPage === 1 ? items : [...prev, ...items]
        );
        setTotalItems(meta.totalItems ?? 0);
      } catch (e) {
        console.error("Failed to load overwritten products", e);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadProducts();

    return () => {
      isActive = false;
    };
  }, [productIdsKey, requestedPage, showId, supplierCompanyId]);

  return {
    labels: products.map(formatProductOverwrittenLabel),
    hasMore: products.length < totalItems,
    isLoading,
    loadMore: () => {
      if (!isLoading) setRequestedPage((prev) => prev + 1);
    },
  };
};

export const getSign = (num: number) => (num >= 0 ? "+" : "-");
